import { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';

import { GqlUpdateWebCustomizationSettingsV2Input } from 'types/graphql';
import { ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';
import { useUI } from 'src/hooks/use-ui';
import { useUser } from 'src/hooks/use-user';
import { useUpdateEnterpriseWebCustomizationSettingsV2 } from './data-access';

type UseApplyToAllLocationsProps = {
  input: Partial<GqlUpdateWebCustomizationSettingsV2Input>;
  afterApplyAll?: () => void;
  sectionName: string;
};

type UseApplyToAllLocationsReturn = {
  /** Apply settings to specific dispensary IDs, or all locations if not provided */
  handleApplyToLocations: (dispensaryIds?: string[]) => void;
  /** @deprecated Use handleApplyToLocations instead */
  handleApplyToAllLocations: () => void;
  hasAccessToAllLocations: boolean;
  chainId: string | undefined;
  totalLocationCount: number;
};

export const useApplyToAllLocations = ({
  input,
  afterApplyAll,
  sectionName,
}: UseApplyToAllLocationsProps): UseApplyToAllLocationsReturn => {
  const showErnie = useErnie();
  const UI = useUI();
  const chainId = UI.dispensary.chain ?? undefined;
  const enterpriseId = UI.dispensary.retailer?.enterpriseId;
  const User = useUser();
  const dispoIdsWithPermission = User.profile.permissions?.dispensaryIds ?? [];
  const chainDispoIds = useObserver(() => UI.chainDispensaryIds);

  // This is a chain, make sure to fetch the chain ids
  useEffect(() => {
    if (chainId) {
      void UI.fetchChainDispensaryIds(chainId);
    }
  }, [UI, chainId]);

  const updateData = useUpdateEnterpriseWebCustomizationSettingsV2();

  /**
   * Apply settings to specific dispensary IDs, or all locations if dispensaryIds is not provided
   */
  const handleApplyToLocations = async (dispensaryIds?: string[]): Promise<void> => {
    if (!enterpriseId) {
      return;
    }

    const locationCount = dispensaryIds?.length ?? UI.chainDispensaryIds.length;
    const isSelectiveUpdate = dispensaryIds && dispensaryIds.length < UI.chainDispensaryIds.length;

    await updateData({
      variables: {
        enterpriseId,
        input,
        dispensaryIds,
      },
    })
      .then(() => {
        const message = isSelectiveUpdate
          ? `${sectionName} has been updated for ${locationCount} selected locations`
          : `${sectionName} has been updated for ${locationCount} locations`;
        showErnie(message, ERNIE_TYPES.SUCCESS);
        afterApplyAll?.();
      })
      .catch((err) => {
        showErnie('Error publishing to locations. Please try again', ERNIE_TYPES.DANGER);
        console.error(err);
      });
  };

  /** @deprecated Use handleApplyToLocations instead */
  const handleApplyToAllLocations = async (): Promise<void> => {
    await handleApplyToLocations();
  };

  const hasAccessToAllLocations =
    (User.isSuperAdmin || dispoIdsWithPermission.length === chainDispoIds.length) && chainDispoIds.length > 0;

  return {
    handleApplyToLocations,
    handleApplyToAllLocations,
    hasAccessToAllLocations,
    chainId,
    totalLocationCount: chainDispoIds.length,
  };
};
